import { Box, useMediaQuery, useTheme } from "@mui/material";
import { useState } from "react";
import { Outlet } from "react-router-dom";
import { AppBottomNav, BOTTOM_NAV_HEIGHT } from "../components/app/AppBottomNav";
import { AppSidebar } from "../components/app/AppSidebar";
import { AppTopNavbar } from "../components/app/AppTopNavbar";
import { useAuth } from "../auth/AuthContext";
import { useAcademicYearContext } from "../context/AacademicYearContext";
import LoadingDialog from "../modules/common/shared/util/LoadingDialog";
import { Constants } from "../modules/common/shared/Constants";

const RAIL_PREF_KEY = Constants.SIDEBAR_EXPANDED; // reset at every login (saveLoggedInUserData)

const readExpanded = (): boolean => {
  try {
    return localStorage.getItem(RAIL_PREF_KEY) === "true"; // collapsed (icon rail) unless the user expanded it
  } catch {
    return false;
  }
};

/**
 * App shell. Desktop: permanent sidebar that ☰ collapses to an icon rail (remembered).
 * Phone: the menu is a slide-over drawer, plus a bottom bar with the most-used pages.
 */
export const AppLayout = () => {
  const theme = useTheme();
  // noSsr: read the real width on the first render, so desktop never flashes the phone drawer/bottom bar
  const isDesktop = useMediaQuery(theme.breakpoints.up("md"), { noSsr: true });
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [expanded, setExpanded] = useState<boolean>(readExpanded);
  const { loaded: authLoaded } = useAuth();
  const { loaded: academicYearLoaded } = useAcademicYearContext();

  if (!authLoaded || !academicYearLoaded) return <LoadingDialog open />;

  const toggleMenu = () => {
    if (!isDesktop) {
      setDrawerOpen((open) => !open);
      return;
    }
    setExpanded((prev) => {
      try {
        localStorage.setItem(RAIL_PREF_KEY, String(!prev));
      } catch {
        // private mode / blocked storage: the choice just isn't remembered
      }
      return !prev;
    });
  };

  return (
    <Box sx={{ display: "flex", height: "100vh" }}>
      {isDesktop ? (
        <AppSidebar mode="permanent" expanded={expanded} />
      ) : (
        <AppSidebar mode="drawer" open={drawerOpen} onClose={() => setDrawerOpen(false)} />
      )}

      <Box sx={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column" }}>
        <AppTopNavbar onToggleSidebar={toggleMenu} />

        <Box
          sx={{
            flex: 1,
            overflow: "auto",
            // The grey canvas fills the whole content area edge to edge (no white strips beside the
            // sidebar or under the navbar); all spacing is padding, so content sits where it did before
            // and the scrollbar stays at the window edge.
            background: "#f6f8fb",
            pt: "18px",
            pl: { md: "32px", xs: "18px" },
            pr: { md: "20px", xs: "10px" },
            // keep the last rows clear of the phone bottom bar
            pb: { xs: `${BOTTOM_NAV_HEIGHT + 16}px`, md: 1 },
          }}
        >
          <Outlet />
        </Box>
      </Box>

      {!isDesktop && <AppBottomNav onMore={() => setDrawerOpen(true)} />}
    </Box>
  );
};
